import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export interface MultiSelectOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  isHeader?: boolean;
  parentTypeId?: string;
  parentTypeName?: string;
}

interface MultiSelectDropdownWithFieldProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onBlur?: (value: string | string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  maxHeight?: string;
  showSelectedOptions?: boolean; // New prop to control visibility of selected options display
}

export const MultiSelectDropdownWithField = React.forwardRef<HTMLDivElement, MultiSelectDropdownWithFieldProps>(({
  value,
  onChange,
  onBlur,
  options,
  placeholder = "Select options...",
  multiple = false,
  disabled = false,
  isLoading = false,
  className = "",
  maxHeight = "max-h-[300px]",
  showSelectedOptions = true, // Default to true for backward compatibility
}, ref) => {

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<'down' | 'up'>('down');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  // Convert value to array for easier handling
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const { t } = usePlatformTranslation();

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate dropdown position to ensure it's always visible
  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return 'down';
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 300; // Max height of dropdown
    const dropdownWidth = buttonRect.width;
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    // Check horizontal positioning
    const spaceRight = viewportWidth - buttonRect.left;
    const spaceLeft = buttonRect.right;
    
    // If not enough space below but enough above, position up
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return 'up';
    }
    
    // If not enough space on the right, adjust left position
    if (spaceRight < dropdownWidth) {
      // Could add left positioning logic here if needed
    }
    
    return 'down';
  };

  // Handle option selection/deselection
  const handleOptionToggle = (optionId: string) => {
    // Find the option to check if it's disabled or a header
    const option = options.find(opt => opt.id === optionId);
    if (option?.disabled || option?.isHeader) {
      return; // Don't allow selection of disabled or header options
    }

    if (multiple) {
      const newValues = selectedValues.includes(optionId)
        ? selectedValues.filter(id => id !== optionId)
        : [...selectedValues, optionId];
      onChange(newValues);
      if (onBlur) onBlur(newValues);
    } else {
      const newValue = selectedValues.includes(optionId) ? "" : optionId;
      onChange(newValue);
      if (onBlur) onBlur(newValue);
      setIsOpen(false);
    }
  };

  // Handle remove selected option
  const handleRemoveOption = (optionId: string) => {
    if (multiple) {
      const newValues = selectedValues.filter(id => id !== optionId);
      onChange(newValues);
      if (onBlur) onBlur(newValues);
    } else {
      onChange("");
      if (onBlur) onBlur("");
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    if (multiple) {
      // Only clear enabled options, keep disabled ones (like blueprint tags)
      const disabledValues = selectedValues.filter(id => {
        const option = options.find(opt => opt.id === id);
        return option && option.disabled;
      });
      onChange(disabledValues);
      if (onBlur) onBlur(disabledValues);
    } else {
      onChange("");
      if (onBlur) onBlur("");
    }
  };

  // Get selected option names for display
  const getSelectedOptionNames = () => {
    return selectedValues
      .map(id => options.find(option => option.id === id)?.label)
      .filter(Boolean);
  };

  // Get enabled selected option names (excluding disabled options like blueprint tags)
  const getEnabledSelectedOptionNames = () => {
    return selectedValues
      .filter(id => {
        const option = options.find(opt => opt.id === id);
        return option && !option.disabled;
      })
      .map(id => options.find(option => option.id === id)?.label)
      .filter(Boolean);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent scroll propagation when scrolling inside dropdown
  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (dropdownContentRef.current && dropdownContentRef.current.contains(event.target as Node)) {
        event.stopPropagation();
      }
    };

    // Add scroll event listener to prevent background scrolling
    document.addEventListener('scroll', handleScroll, { passive: false, capture: true });
    
    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, []);

  // Calculate position when opening dropdown
  const handleToggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        const position = calculateDropdownPosition();
        setDropdownPosition(position);
      }
      setIsOpen(!isOpen);
    }
  };

  // Handle window resize to recalculate position
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        const position = calculateDropdownPosition();
        setDropdownPosition(position);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Get display text for the button
  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    
    if (multiple) {
      // Get all selected names (including blueprint tags)
      const allSelectedNames = getSelectedOptionNames();
      
      // Filter out disabled options (like blueprint tags) from the count
      const enabledSelectedValues = selectedValues.filter(id => {
        const option = options.find(opt => opt.id === id);
        return option && !option.disabled;
      });
      
      if (enabledSelectedValues.length === 0) {
        // Only disabled options selected (blueprint tags only), show them
        if (allSelectedNames.length === 1) return allSelectedNames[0];
        if (allSelectedNames.length === 2) return `${allSelectedNames[0]}, ${allSelectedNames[1]}`;
        if (allSelectedNames.length > 2) return `${allSelectedNames[0]}, ${allSelectedNames[1]} +${allSelectedNames.length - 2}`;
      } else {
        // Show blueprint tags + custom tags count
        const disabledSelectedValues = selectedValues.filter(id => {
          const option = options.find(opt => opt.id === id);
          return option && option.disabled;
        });
        
        const disabledNames = disabledSelectedValues
          .map(id => options.find(option => option.id === id)?.label)
          .filter(Boolean);
        
        // Show first 2 blueprint tags + custom tags count
        let displayText = "";
        if (disabledNames.length > 0) {
          if (disabledNames.length === 1) {
            displayText = disabledNames[0];
          } else if (disabledNames.length === 2) {
            displayText = `${disabledNames[0]}, ${disabledNames[1]}`;
          } else {
            displayText = `${disabledNames[0]}, ${disabledNames[1]} +${disabledNames.length - 2}`;
          }
        }
        
        // Add custom tags
        if (enabledSelectedValues.length === 1) {
          const customName = allSelectedNames.find(name => 
            !disabledNames.includes(name)
          );
          if (displayText) {
            displayText += `, ${customName}`;
          } else {
            displayText = customName;
          }
        } else if (enabledSelectedValues.length === 2) {
          const customNames = allSelectedNames.filter(name => 
            !disabledNames.includes(name)
          );
          if (displayText) {
            displayText += `, ${customNames[0]}, ${customNames[1]}`;
          } else {
            displayText = `${customNames[0]}, ${customNames[1]}`;
          }
        } else {
          const customNames = allSelectedNames.filter(name => 
            !disabledNames.includes(name)
          );
          if (displayText) {
            displayText += `, ${customNames[0]}, ${customNames[1]} +${customNames.length - 2}`;
          } else {
            displayText = `${customNames[0]}, ${customNames[1]} +${customNames.length - 2}`;
          }
        }
        
        return displayText;
      }
    } else {
      const selectedName = options.find(option => option.id === selectedValues[0])?.label;
      return selectedName || placeholder;
    }
    
    return placeholder;
  };

  return (
    <div className={cn("relative", className)} ref={ref || dropdownRef}>
      {/* Main button */}
      <Button
        ref={buttonRef}
        type="button"
        variant="outline"
        className={cn(
          "bg-background w-full justify-between text-left font-normal rounded border-input",
          !selectedValues.length && "text-muted-foreground",
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onClick={handleToggleDropdown}
        disabled={disabled}
      >
        <span className="truncate">{getDisplayText()}</span>
        <ChevronDown className={cn(
          "h-4 w-4 shrink-0 transition-transform",
          isOpen && "rotate-180"
        )} />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className={cn(
            "absolute z-50 w-full bg-popover border border-border rounded-sm overflow-hidden shadow-lg",
            dropdownPosition === 'down' ? "mt-1 top-full" : "mb-1 bottom-full"
          )}
          style={{
            maxHeight: dropdownPosition === 'down' ? '300px' : '300px',
            overflow: 'hidden'
          }}
        >
          {/* Header with search and clear */}
          <div className="p-3 border-b border-border bg-popover sticky top-0 z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                {multiple ? "Select 1 or more options" : "Select only 1 option"}
              </span>
              {getEnabledSelectedOptionNames().length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear custom
                </Button>
              )}
            </div>
            
            {/* Search input */}
            <input
              type="text"
              dir="auto"
              placeholder={t('platform.ui.dropdowns.search_options')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-input rounded-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>

          {/* Options list with proper scrolling */}
          <div 
            ref={dropdownContentRef}
            className={cn(
              "overflow-y-auto overflow-x-hidden custom-scrollbar",
              maxHeight
            )}
            style={{
              maxHeight: 'calc(300px - 120px)', // Subtract header height
              scrollbarWidth: 'thin',
              WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
            }}
            onWheel={(e) => {
              // Prevent scroll propagation to parent elements
              e.stopPropagation();
            }}
            onScroll={(e) => {
              // Prevent scroll propagation to parent elements
              e.stopPropagation();
            }}
          >
            {isLoading ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                Loading options...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                {searchTerm ? "No options found matching your search" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.id);
                const isHeader = option.isHeader;
                const isDisabled = option.disabled;
                
                return (
                  <div
                    key={option.id}
                    className={cn(
                      "flex items-center px-3 py-2",
                      isHeader 
                        ? "bg-muted border-b border-border cursor-default" 
                        : "cursor-pointer hover:bg-muted",
                      isSelected && !isHeader && "bg-primary/10",
                      isDisabled && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => !isHeader && !isDisabled && handleOptionToggle(option.id)}
                  >
                    {!isHeader && (
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 pointer-events-none"
                        disabled={isDisabled}
                      />
                    )}
                    <span className={cn(
                      "text-sm",
                      isHeader 
                        ? "font-semibold text-foreground" 
                        : "text-foreground"
                    )}>
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="ml-2 text-xs text-muted-foreground truncate">
                        {option.description}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Selected options display (for multiple selection) */}
      {multiple && showSelectedOptions && selectedValues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {getEnabledSelectedOptionNames().map((name, index) => {
            // Find the corresponding option ID for removal
            const optionId = selectedValues.find(id => {
              const option = options.find(opt => opt.id === id);
              return option && !option.disabled && option.label === name;
            });
            
            if (!optionId) return null;
            
            return (
              <div
                key={optionId}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-muted text-foreground rounded-md"
              >
                {name}
                <button
                  type="button"
                  onClick={() => handleRemoveOption(optionId)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

MultiSelectDropdownWithField.displayName = "MultiSelectDropdownWithField";
